import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useRouteError,
} from "react-router";

function RouteErrorBoundary(): JSX.Element {
  const error = useRouteError();
  throw error;
}

// Toolkit integrations
import { useSegment } from "sj-ui-toolkit/src/hooks";
import { SettingsContextProvider } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";
import { ApiCacheProvider } from "sj-ui-toolkit/src/api/cache";

// App context
import { AppProvider } from "@/context/AppProvider";

// Layout
import Layout from "@/components/layout/Layout";

// Pages
const Dashboard = lazy(() => import("@/pages/Dashboard"));
import Settings from "@/pages/Settings";
import KeyManagement from "@/pages/KeyManagement";
import AspenSettings from "@/pages/AspenSettings";
import AspenAssist from "@/pages/AspenAssist";
import AspenGuardian from "@/pages/AspenGuardian";
import AspenAdapt from "@/pages/AspenAdapt";
import CustomResources from "@/pages/CustomResources";
import Connections from "@/pages/Connections";
import ReportingApiConnection from "@/pages/connections/ReportingApiConnection";
import AspenConnection from "@/pages/connections/AspenConnection";
import McpConnection from "@/pages/connections/McpConnection";
import SSOConnection from "@/pages/connections/SSOConnection";
import ScimConnection from "@/pages/connections/ScimConnection";
import XapiConnection from "@/pages/connections/XapiConnection";
import WebhooksConnection from "@/pages/connections/WebhooksConnection";
import { BellNotifications } from "@/pages/BellNotifications";
import NotFound from "@/pages/NotFound";
import AssignmentsReport from "@/pages/reports/AssignmentsReport";
import ProgressReport from "@/pages/reports/ProgressReport";
import ProgressReportUser from "@/pages/reports/ProgressReportUser";
import RankingsReport from "@/pages/reports/RankingsReport";
import UsageReport from "@/pages/reports/UsageReport";
import ReportsDownload from "@/pages/reports/ReportsDownload";
import AssessmentsReport from "@/pages/reports/AssessmentsReport";
import AssessmentReportDetail from "@/pages/reports/AssessmentReportDetail";
import AssessmentReportUser from "@/pages/reports/AssessmentReportUser";
import PersonalizedLessonAssignmentReport from "@/pages/reports/PersonalizedLessonAssignmentReport";
import CommsBanner from "@/pages/comms/CommsBanner";
import Assignments from "@/pages/Assignments";
import AssignmentDetails from "@/pages/AssignmentDetails";
import NewAssignment from "@/pages/NewAssignment";
import RecommendedAssignment from "@/pages/RecommendedAssignment";
import PersonalizedLessonAssignment from "@/pages/PersonalizedLessonAssignment";
import PersonalizedLessonAssignmentEdit from "@/pages/PersonalizedLessonAssignmentEdit";
import KnowledgeAssessments from "@/pages/KnowledgeAssessments";
import KnowledgeAssessmentsNew from "@/pages/KnowledgeAssessmentsNew";
import { CommsEmails } from "@/pages/comms/CommsEmails";
import { CommsEmailEdit } from "@/pages/comms/CommsEmailEdit";
import { ActivityConfiguration } from "@/pages/ActivityConfiguration";
import MentorRequests from "@/pages/champions/MentorRequests";
import EvaluateRequests from "@/pages/champions/EvaluateRequests";
import RequestDetail from "@/pages/champions/RequestDetail";
import Users from "@/pages/Users";
import UserDetail from "@/pages/users/UserDetail";
import UserProfileTab from "@/pages/users/UserProfileTab";
import UserHistoryTab from "@/pages/users/UserHistoryTab";
import UserCertificatesTab from "@/pages/users/UserCertificatesTab";
import Tournaments from "@/pages/Tournaments";
import TournamentForm from "@/pages/tournaments/TournamentForm";
import TournamentLeaderboard from "@/pages/tournaments/TournamentLeaderboard";
import AuditLog from "@/pages/AuditLog";

// Paths & Quests
import PathsCustom from "@/pages/paths/PathsCustom";
import PathsRecommended from "@/pages/paths/PathsRecommended";
import Quests from "@/pages/paths/Quests";
import PathsNew from "@/pages/paths/PathsNew";
import PathDetail from "@/pages/paths/PathDetail";
import { PathsProvider } from "@/context/PathsContext";
import { MentorRequestsProvider } from "@/context/MentorRequestsContext";
import { EvaluateRequestsProvider } from "@/context/EvaluateRequestsContext";

// Routes
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Main shell — Atlas layout with top nav + side panel */}
      <Route
        path={AdminAppRoutes.route}
        errorElement={<RouteErrorBoundary />}
        element={
          <AppProvider>
            <ApiCacheProvider>
              <SettingsContextProvider>
                <Layout />
              </SettingsContextProvider>
            </ApiCacheProvider>
          </AppProvider>
        }
      >
        {/* Dashboard */}
        <Route
          index
          element={
            <Suspense fallback={null}>
              <Dashboard />
            </Suspense>
          }
        />
        {/* Settings */}
        <Route path={AdminAppRoutes.routes.settings.route} element={<Settings />} />
        {/* Key Management */}
        <Route path={AdminAppRoutes.routes.keyManagement.route} element={<KeyManagement />} />
        {/* Aspen */}
        <Route
          path={AdminAppRoutes.routes.aspen.route}
          element={<Navigate to={AdminAppRoutes.routes.aspen.routes.settings.route} replace />}
        />
        <Route
          path={AdminAppRoutes.routes.aspen.routes.settings.route}
          element={<AspenSettings />}
        />
        <Route path={AdminAppRoutes.routes.aspen.routes.assist.route} element={<AspenAssist />} />
        <Route
          path={AdminAppRoutes.routes.aspen.routes.guardian.route}
          element={<AspenGuardian />}
        />
        <Route path={AdminAppRoutes.routes.aspen.routes.adapt.route} element={<AspenAdapt />} />
        {/* Custom Resources */}
        <Route path={AdminAppRoutes.routes.resources.route} element={<CustomResources />} />
        {/* Activity Configuration */}
        <Route
          path={AdminAppRoutes.routes.champions.routes.activities.route}
          element={<ActivityConfiguration />}
        />
        {/* Mentor Requests — context-wrapped so list and detail share state */}
        <Route element={<MentorRequestsProvider />}>
          <Route
            path={AdminAppRoutes.routes.champions.routes.mentorRequests.route}
            element={<MentorRequests />}
          />
          <Route
            path={AdminAppRoutes.routes.champions.routes.mentorRequests.routes.detail.route}
            element={<RequestDetail />}
          />
        </Route>
        {/* Evaluation Requests — context-wrapped so list and detail share state */}
        <Route element={<EvaluateRequestsProvider />}>
          <Route
            path={AdminAppRoutes.routes.champions.routes.evaluateRequests.route}
            element={<EvaluateRequests />}
          />
          <Route
            path={AdminAppRoutes.routes.champions.routes.evaluateRequests.routes.detail.route}
            element={<RequestDetail />}
          />
        </Route>
        {/* Connections */}
        <Route path={AdminAppRoutes.routes.connections.route} element={<Connections />} />
        <Route
          path={AdminAppRoutes.routes.connections.routes.reportingApi.route}
          element={<ReportingApiConnection />}
        />
        <Route
          path={AdminAppRoutes.routes.connections.routes.aspen.route}
          element={<AspenConnection />}
        />
        <Route
          path={AdminAppRoutes.routes.connections.routes.mcp.route}
          element={<McpConnection />}
        />
        <Route
          path={AdminAppRoutes.routes.connections.routes.sso.route}
          element={<SSOConnection />}
        />
        <Route
          path={AdminAppRoutes.routes.connections.routes.loginSettings.route}
          element={<SSOConnection />}
        />
        <Route
          path={AdminAppRoutes.routes.connections.routes.scim.route}
          element={<ScimConnection />}
        />
        <Route
          path={AdminAppRoutes.routes.connections.routes.xapi.route}
          element={<XapiConnection />}
        />
        <Route
          path={AdminAppRoutes.routes.connections.routes.webhooks.route}
          element={<WebhooksConnection />}
        />

        {/* Assignments */}
        <Route path={AdminAppRoutes.routes.assignments.route} element={<Assignments />} />
        <Route
          path={AdminAppRoutes.routes.assignments.routes.new.route}
          element={<NewAssignment />}
        />
        <Route
          path={AdminAppRoutes.routes.assignments.routes.id.route}
          element={<AssignmentDetails />}
        />
        <Route
          path={AdminAppRoutes.routes.assignments.routes.edit.route}
          element={<NewAssignment />}
        />
        {/* Personalized Lesson Assignment */}
        <Route
          path={AdminAppRoutes.routes.assignments.routes.personalizedLessonAssignment.route}
          element={<PersonalizedLessonAssignment />}
        />
        <Route
          path={
            AdminAppRoutes.routes.assignments.routes.personalizedLessonAssignment.routes.edit.route
          }
          element={<PersonalizedLessonAssignmentEdit />}
        />
        {/* Recommended Assignment */}
        <Route
          path={AdminAppRoutes.routes.assessments.routes.recommendedAssignment.route}
          element={<RecommendedAssignment />}
        />
        {/* Knowledge Assessments */}
        <Route path={AdminAppRoutes.routes.assessments.route} element={<KnowledgeAssessments />} />
        <Route
          path={AdminAppRoutes.routes.assessments.routes.new.route}
          element={<KnowledgeAssessmentsNew />}
        />
        <Route
          path={AdminAppRoutes.routes.assessments.routes.id.route}
          element={<KnowledgeAssessmentsNew />}
        />
        {/* Users */}
        <Route path={AdminAppRoutes.routes.users.route} element={<Users />} />
        <Route path={AdminAppRoutes.routes.users.routes.id.route} element={<UserDetail />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<UserProfileTab />} />
          <Route path="history" element={<UserHistoryTab />} />
          <Route path="certificates" element={<UserCertificatesTab />} />
        </Route>
        {/* Communications */}
        <Route path={AdminAppRoutes.routes.comms.routes.banner.route} element={<CommsBanner />} />
        <Route
          path={AdminAppRoutes.routes.comms.routes.notify.route}
          element={<BellNotifications />}
        />
        <Route path={AdminAppRoutes.routes.comms.routes.email.route} element={<CommsEmails />} />
        <Route
          path={`${AdminAppRoutes.routes.comms.routes.email.route}/:template`}
          element={<CommsEmailEdit />}
        />
        {/* Reporting */}
        <Route
          path={AdminAppRoutes.routes.reporting.routes.rankings.route}
          element={<RankingsReport />}
        />
        <Route
          path={AdminAppRoutes.routes.reporting.routes.assignments.route}
          element={<AssignmentsReport />}
        />
        <Route
          path={
            AdminAppRoutes.routes.reporting.routes.assignments.routes.personalizedLessonAssignments
              .route
          }
          element={<PersonalizedLessonAssignmentReport />}
        />
        <Route
          path={AdminAppRoutes.routes.reporting.routes.progress.route}
          element={<ProgressReport />}
        />
        <Route
          path={AdminAppRoutes.routes.reporting.routes.progressId.route}
          element={<ProgressReportUser />}
        />
        <Route
          path={AdminAppRoutes.routes.reporting.routes.usage.route}
          element={<UsageReport />}
        />
        <Route
          path={AdminAppRoutes.routes.reporting.routes.download.route}
          element={<ReportsDownload />}
        />
        <Route
          path={AdminAppRoutes.routes.reporting.routes.assessments.route}
          element={<AssessmentsReport />}
        />
        <Route
          path={AdminAppRoutes.routes.reporting.routes.assessmentId.route}
          element={<AssessmentReportDetail />}
        />
        <Route
          path={AdminAppRoutes.routes.reporting.routes.assessmentId.routes.userId.route}
          element={<AssessmentReportUser />}
        />
        {/* Tournaments */}
        <Route path={AdminAppRoutes.routes.tournaments.route} element={<Tournaments />} />
        <Route
          path={AdminAppRoutes.routes.tournaments.routes.new.route}
          element={<TournamentForm />}
        />
        <Route
          path={AdminAppRoutes.routes.tournaments.routes.id.route}
          element={<TournamentForm />}
        />
        <Route
          path={AdminAppRoutes.routes.tournaments.routes.leaderboard.route}
          element={<TournamentLeaderboard />}
        />
        {/* Paths & Quests */}
        <Route
          element={
            <PathsProvider>
              <Outlet />
            </PathsProvider>
          }
        >
          <Route path={AdminAppRoutes.routes.paths.routes.custom.route} element={<PathsCustom />} />
          <Route
            path={AdminAppRoutes.routes.paths.routes.recommended.route}
            element={<PathsRecommended />}
          />
          <Route path={AdminAppRoutes.routes.paths.routes.quests.route} element={<Quests />} />
          <Route path={AdminAppRoutes.routes.paths.routes.new.route} element={<PathsNew />} />
          <Route path={AdminAppRoutes.routes.paths.routes.edit.route} element={<PathsNew />} />
          <Route path={AdminAppRoutes.routes.paths.routes.detail.route} element={<PathDetail />} />
          <Route
            path={AdminAppRoutes.routes.paths.routes.detailBySource.route}
            element={<PathDetail />}
          />
        </Route>
        {/* Audit Log */}
        <Route path={AdminAppRoutes.routes.auditLog.route} element={<AuditLog />} />
      </Route>
      {/* 404 — catches any unmatched routes */}
      <Route path="*" element={<NotFound />} />
    </>
  )
);

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

function App() {
  useSegment();
  return <RouterProvider router={router} />;
}

export default App;
